import React, {useMemo} from "react";
import DOMPurify from "dompurify";

// Defence-in-depth against mXSS (the api already sanitizes server-side, but
// a real browser can re-parse markup differently than jsdom). Broad on
// purpose — not the api's narrow per-field allowlist — so it doesn't clip
// any editor/embed markup the portals already render; FORBID_TAGS is the
// actual safety net.
const SAFE_HTML_CONFIG: DOMPurify.Config = {
  ADD_TAGS: ["iframe"],
  ADD_ATTR: ["target", "allowfullscreen", "frameborder"],
  FORBID_TAGS: [
    "script",
    "style",
    "object",
    "embed",
    "form",
    "input",
    "button",
    "textarea",
    "select",
    "template",
    "noscript",
    "base",
    "svg",
    "math",
  ],
};

export const sanitizeHtml = (value: string): string =>
  DOMPurify.sanitize(value, SAFE_HTML_CONFIG);

const EDITOR_ALLOWED_URI_REGEXP = /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix|blob):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

export const sanitizeEditorInput = (value: string): string =>
  DOMPurify.sanitize(value, {
    ...SAFE_HTML_CONFIG,
    ALLOWED_URI_REGEXP: EDITOR_ALLOWED_URI_REGEXP,
  });

type SafeHtmlProps = {
  html?: string | null;
  className?: string;
  style?: React.CSSProperties;
  /** Keeps `blob:` images an editor has not uploaded yet — for previews of unsaved content. */
  allowPendingImages?: boolean;
};

// Drop-in replacement for a raw dangerouslySetInnerHTML div. Call sites that
// need a ref (RichText, GlossaryTermDescription) use sanitizeHtml() directly
// on their own <div> instead, via the same sanitizer.
const SafeHtml: React.FC<SafeHtmlProps> = ({
  html,
  className,
  style,
  allowPendingImages = false,
}) => {
  const clean = useMemo(() => {
    if (!html) return html;
    return allowPendingImages ? sanitizeEditorInput(html) : sanitizeHtml(html);
  }, [html, allowPendingImages]);

  if (!clean) {
    return null;
  }

  return (
    <div
      className={className}
      style={style}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{__html: clean}}
    />
  );
};

export default React.memo(SafeHtml);
